import { Alert } from '@televet/kibble-ui/Alert';
import { Select } from '@televet/kibble-ui/Select';
import { Text } from '@televet/kibble-ui/Text';
import { Flex } from '@televet/kibble-ui/chakra';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useMemo } from 'react';
import { z } from 'zod';
import { Breadcrumbs } from '@/components/Breadcrumbs';
import { CopyableId } from '@/components/CopyableId';
import { Field } from '@/components/Field';
import { PageContainer } from '@/components/PageContainer';
import { SummaryCard } from '@/components/SummaryCard';
import { trpc } from '@/lib/trpc';
import { ConditionCard } from './-components/ConditionCard';
import { SignalsTable } from './-components/SignalsTable';
import type { ExplainedRuleRef, SignalRow } from './-lib/types';

const ALL_SOURCES = '__all__';

const searchSchema = z.object({
  sourceType: z.string().optional(),
});

const RouteComponent = () => {
  const { petId } = Route.useParams();
  const navigate = Route.useNavigate();
  const { sourceType } = Route.useSearch();

  const { data, isLoading, isError, error } = useQuery(
    trpc.petConditions.getPet.queryOptions({ petId, ...(sourceType ? { sourceType } : {}) })
  );

  const signalsById = useMemo(
    () => new Map((data?.signals ?? []).map((signal) => [signal.id, signal])),
    [data?.signals]
  );

  const ruleRefsById = useMemo(
    () => new Map<string, ExplainedRuleRef>((data?.ruleRefs ?? []).map((ref) => [ref.id, ref])),
    [data?.ruleRefs]
  );

  const waitingOnPairSignalIds = useMemo(
    () => new Set(data?.waitingOnPairSignalIds ?? []),
    [data?.waitingOnPairSignalIds]
  );

  const sourceOptions = useMemo(
    () => [
      { label: 'All sources', value: ALL_SOURCES },
      ...(data?.sourceTypes ?? []).map((type) => ({ label: type, value: type })),
    ],
    [data?.sourceTypes]
  );

  const selectSource = (next: string | undefined) => {
    navigate({ search: { sourceType: next === ALL_SOURCES ? undefined : next } });
  };

  if (isLoading) {
    return (
      <PageContainer>
        <Flex justify="center" p={8}>
          <Text variant="subtle">Loading pet…</Text>
        </Flex>
      </PageContainer>
    );
  }

  // An error, not an empty pet. getPet always returns an object, so reaching
  // here without data means the query failed, and the first person to hit it
  // will be a non-admin getting FORBIDDEN: the route tree only guards on the
  // client. Saying "no data" would send QA after the wrong bug.
  if (isError || !data) {
    return (
      <PageContainer>
        <Alert
          status="error"
          hideCloseButton
          title="Could not load this pet"
          description={
            error?.message ?? 'The request failed. This does not mean the pet has no data.'
          }
        />
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Flex direction="column" w="100%" gap={4}>
        <Breadcrumbs
          items={[
            { label: 'Pet Conditions', to: '/pet-conditions' },
            { label: data.pet.name ?? petId },
          ]}
        />

        <Text fontSize="lg" fontWeight="semibold" letterSpacing="-0.01em">
          {data.pet.name ?? 'Unnamed pet'}
        </Text>

        <Flex gap={4} wrap="wrap">
          <SummaryCard title="PET">
            <Field label="Species">
              <Text fontSize="sm" color={data.pet.species ? undefined : 'text.subtle'}>
                {data.pet.species ?? '—'}
              </Text>
            </Field>
            <Field label="Pet ID" minW="200px">
              <CopyableId id={data.pet.id} label="pet" />
            </Field>
          </SummaryCard>

          <SummaryCard title="CLINIC">
            <Field label="Name" minW="180px">
              <Text fontSize="sm" color={data.pet.clinicName ? undefined : 'text.subtle'}>
                {data.pet.clinicName ?? 'Name not found'}
              </Text>
            </Field>
            <Field label="Clinic ID" minW="200px">
              {data.pet.clinicId ? (
                <CopyableId id={data.pet.clinicId} label="clinic" />
              ) : (
                <Text fontSize="sm" color="text.subtle">
                  —
                </Text>
              )}
            </Field>
          </SummaryCard>
        </Flex>

        <Text fontSize="md" fontWeight="semibold">
          Conditions
        </Text>

        {data.recomputeFailingRuleIds.length > 0 && (
          <Alert
            status="error"
            hideCloseButton
            title="The job cannot recompute this pet"
            description={`Rule ${data.recomputeFailingRuleIds.join(', ')} could not be evaluated, which fails the job's compute for the whole pet. Until it is fixed, no condition below changes, whatever a card says a recompute would do.`}
            data-testid="pet-conditions-recompute-blocked"
          />
        )}

        {data.states.length === 0 ? (
          <Flex justify="center" p={6}>
            <Text variant="subtle">
              No condition has been computed for this pet. Signals below, if any, have not rolled up
              yet.
            </Text>
          </Flex>
        ) : (
          <Flex direction="column" gap={3}>
            {data.states.map((state) => (
              <ConditionCard
                key={state.conditionConceptId}
                state={state}
                signalsById={signalsById}
                ruleRefsById={ruleRefsById}
                recomputeBlocked={data.recomputeFailingRuleIds.length > 0}
              />
            ))}
          </Flex>
        )}

        <Flex justify="space-between" align="flex-end" wrap="wrap" gap={2}>
          <Flex direction="column" gap={1}>
            <Text fontSize="md" fontWeight="semibold">
              Signals
            </Text>
            <Text variant="subtle" fontSize="sm">
              Voided rows are shown, dimmed. A condition usually changes because a signal was
              superseded.
            </Text>
          </Flex>
          <Flex minW="260px">
            <Select
              label="Source"
              options={sourceOptions}
              value={[sourceType ?? ALL_SOURCES]}
              onValueChange={(details) => selectSource(details.value[0])}
              data-testid="pet-conditions-source-select"
            />
          </Flex>
        </Flex>

        {data.signals.length === 0 ? (
          <Flex justify="center" p={6}>
            <Text variant="subtle">No signals for this filter.</Text>
          </Flex>
        ) : (
          <SignalsTable
            signals={data.signals}
            taxonomyLookupAvailable={data.taxonomyLookupAvailable}
            waitingOnPairSignalIds={waitingOnPairSignalIds}
          />
        )}
      </Flex>
    </PageContainer>
  );
};

export const Route = createFileRoute('/_authenticated/pet-conditions/$petId')({
  component: RouteComponent,
  validateSearch: searchSchema,
});
